// restrictionRuleKey — what counts as THE SAME restriction rule, on the Desk.
//
// ⚠️ THIS IS A TWIN of the extension's `restriction_rule_key.js` (`sntvRuleKey`), which the
// gallery page and the service worker both load. Same algorithm, different module system —
// the extension file is a classic script (importScripts + <script>), this one is ESM. The
// parity check `core/parity/restriction-rule-key-parity.mjs` runs both implementations over
// the same cases and goes red on any disagreement; it skips loudly (exit 0) when the
// extension tree is not on the machine, the same contract the sentence twin's gate has.
//
// Why they must agree: the gallery composes rules locally and the background diffs that
// screen against core at save time, while the Desk POSTs each rule as it is recorded. Both
// surfaces answer "have I already stated this?" and "is this the same question, answered
// differently?" — if the two spellings of identity drifted, one surface would add a
// duplicate the other cannot see, or replace an answer the other thinks is untouched.
//
// ⚠️ CANONICAL, not literal. Axes are sorted and each axis's values are sorted, so
// {sex, nationality} and {nationality, sex} are ONE key — the agent stated one restriction
// and two orderings of it must never become two rows.
//
// ⚠️ IDENTITY HERE IS NOT IDENTITY IN THE DATABASE, and that is on purpose. Core 0399 carries
// no unique constraint: two AGENTS stating the same restriction are two independent
// observations, which is exactly what evidence-certainty reads as corroboration. This key
// answers a narrower question — "is this the same rule that is already on this screen" — and
// is only ever applied within one agent's own set.
//
// The `id` is not part of the key: a rule composed locally has none until core assigns one,
// and keying on it would make every unsaved rule look new forever.

// ⚠️ AN AXIS PRESENT WITH NO VALUES IS NOT AN AXIS — the same rule every reader of a
// predicate obeys (see livePredicate in restrictionSentence.js). The exception builder keeps
// its parent's axes on screen after they are cleared, so a draft legitimately carries
// `{pet_species: [], pet_size: ['small']}`, and keying it literally would give one rule two
// spellings: the one it had while being composed, and the one that reaches the server.
export function livePredicateOf(predicate) {
  const out = {}
  for (const [k, v] of Object.entries(predicate || {})) {
    if (Array.isArray(v) ? v.length > 0 : v != null) out[k] = v
  }
  return out
}

// ⚠️ `negotiable` IS PART OF THE ANSWER (core 0927, added 2026-09-06). "No pets" and "no pets,
// but ask me" share a polarity AND a predicate, so without this they collapse to one key — and
// the writer's double-tap guard (`rules.some(r => ruleKey(r) === ruleKey(shaped))`) then reads a
// genuine change of answer as the agent pressing the same chip twice and silently records
// NOTHING. Found by tracing the new chip through record() rather than by pressing it, which is
// the only reason it is not a shipped defect: the failure is a no-op, and a no-op looks exactly
// like a slow save.
// ⚠️ It is deliberately NOT in `predicateKey`, which strips everything before the first `|`:
// the two remain the SAME QUESTION, so answering one replaces the other instead of leaving a
// flat refusal live beside a negotiable one.
export function ruleKey(rule) {
  const p = livePredicateOf(rule?.predicate)
  return `${rule?.polarity || ''}${rule?.negotiable === true ? '+neg' : ''}|` + Object.keys(p).sort()
    .map(a => `${a}=${Array.isArray(p[a]) ? [...p[a]].sort().join(',') : JSON.stringify(p[a])}`)
    .join('&')
}

// A PREDICATE IS A QUESTION; A POLARITY IS ITS ANSWER. Same predicate under a different
// polarity is therefore not a second observation — it is the same question answered
// differently, i.e. a correction. Derived from ruleKey rather than re-implemented, so
// "which values count as the same set" keeps exactly one definition.
export function predicateKey(rule) {
  return ruleKey(rule).split('|').slice(1).join('|')
}
